import { useEffect, useState } from 'react';
import { Disclosure, Heading } from 'react-aria-components';
import { useParams } from 'react-router-dom';

import { useAtom } from 'jotai';

import { getSupportPlansByChildID, getSupportPlanDraftsByChildID } from '@/api/getSupportPlans';
import { updateSupportPlanStatus, updateSupportPlanDraftStatus } from '@/api/updateSupportPlans';
import Button from '@/components/Button/Button';
import { UpdateDialog } from '@/components/user/support-plans/UpdateDialog';
import { LineUserAtom } from '@/jotai/jotai';
import { formattedDate } from '@/logics/date/date';
import {
  SupportPlanForLine,
  SupportPlanDraftForLine,
  SupportPlanStatusRequest,
} from '@/types/supportPlan';

const SupportPlanPage = () => {
  const [lineUser, _] = useAtom(LineUserAtom);
  const [openDialogId, setOpenDialogId] = useState<number | null>(null);
  const [dialogType, setDialogType] = useState<'plan' | 'draft'>('plan');
  const [status, setStatus] = useState<'approved' | 'rejected'>('approved');
  const [supportPlans, setSupportPlans] = useState<SupportPlanForLine[]>([]);
  const [supportPlanDrafts, setSupportPlanDrafts] = useState<SupportPlanDraftForLine[]>([]);
  const params = useParams<{ id: string }>();
  const childID = Number(params.id);

  useEffect(() => {
    if (childID && lineUser) {
      fetchAllPlans();
    }
  }, [lineUser, childID]);

  const fetchAllPlans = async () => {
    const token = lineUser?.accessToken ?? '';
    const [plansRes, draftsRes] = await Promise.all([
      getSupportPlansByChildID(token, childID),
      getSupportPlanDraftsByChildID(token, childID),
    ]);

    if (plansRes.status === 200) {
      console.log('supportPlans response:', plansRes.result);
      setSupportPlans(plansRes.result.filter((plan) => plan.status !== 'in_progress'));
    }
    if (draftsRes.status === 200) {
      console.log('supportPlanDrafts response:', draftsRes.result);
      setSupportPlanDrafts(draftsRes.result.filter((draft) => draft.status !== 'in_progress'));
    }
  };

  const handlePDFClick = (e: React.MouseEvent, pdfUrl: string | null | undefined) => {
    e.preventDefault();
    if (pdfUrl) {
      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
    } else {
      console.log('PDF URL is not available');
      alert('PDFファイルが見つかりません');
    }
  };

  const handleActionClick = async (
    planId: number,
    planType: 'plan' | 'draft',
    actionStatus: 'approved' | 'rejected'
  ) => {
    // 承認の場合は直接APIを呼び出す
    if (actionStatus === 'approved') {
      try {
        const token = lineUser?.accessToken ?? '';
        const apiRequest: SupportPlanStatusRequest = {
          status: actionStatus,
        };

        console.log('承認APIリクエスト:', {
          childID,
          planId,
          token: token ? 'あり' : 'なし',
          apiRequest,
          planType,
        });

        let result;
        if (planType === 'draft') {
          result = await updateSupportPlanDraftStatus(childID, planId, token, apiRequest);
        } else {
          result = await updateSupportPlanStatus(childID, planId, token, apiRequest);
        }

        console.log('承認APIレスポンス:', result);

        // 201または200を成功として扱う
        if (result.status === 200 || result.status === 201) {
          // 成功したらリストを更新
          await fetchAllPlans();
          alert('承認が完了しました');
        } else {
          console.error('承認処理が失敗しました。ステータス:', result.status);
          alert(`承認処理に失敗しました。ステータス: ${result.status}`);
        }
      } catch (error) {
        console.error('承認処理でエラーが発生しました:', error);
        alert('承認処理に失敗しました');
      }
    } else {
      // 差戻しの場合はダイアログを表示
      setOpenDialogId(planId);
      setDialogType(planType);
      setStatus(actionStatus);
    }
  };

  // デバッグ用:
  useEffect(() => {
    console.log('supportPlans', supportPlans);
    console.log('supportPlanDrafts', supportPlanDrafts);
  }, [supportPlans, supportPlanDrafts]);

  return (
    <div className="m-4 space-y-4">
      <Heading className="text-2xl font-bold">個別支援計画</Heading>

      <div className="rounded-8 bg-blue-100 px-2 py-3">
        <Heading className="text-lg font-bold">
          {supportPlans.length > 0 || supportPlanDrafts.length > 0
            ? (supportPlans[0]?.child_name ?? supportPlanDrafts[0]?.child_name)
            : '個別支援計画はありません'}
        </Heading>
      </div>

      {/* 個別支援計画案の表示 */}
      {supportPlanDrafts.map((plan) => (
        <div
          key={`draft-${plan.id}`}
          className="rounded-8 bg-white p-4 shadow"
        >
          <Disclosure className="h-full rounded-md bg-gray-100">
            <div className="space-y-4 p-4">
              <div className="text-base font-bold">個別支援計画案</div>
              {plan.shared_date && (
                <>
                  <div className="text-base font-bold">共有日</div>
                  <div className="text-base">
                    {formattedDate(new Date(plan.shared_date), 'yyyy年M月d日')}
                  </div>
                </>
              )}
              <hr className="border-gray-400" />
            </div>

            <div className="space-y-4 p-4">
              <a
                href="#"
                onClick={(e) => handlePDFClick(e, plan.pdf_url)}
                className="text-base underline"
              >
                {formattedDate(new Date(plan.created_date), 'yyyy年M月d日')}
              </a>

              <Button
                className="w-full"
                variant={plan.status === 'pending_approval' ? 'solid-fill' : 'disabled'}
                size="lg"
                type="submit"
                disabled={plan.status !== 'pending_approval'}
                onClick={() => handleActionClick(plan.id, 'draft', 'approved')}
              >
                承認する
              </Button>

              <Button
                className="w-full"
                variant={plan.status === 'pending_approval' ? 'text' : 'disabled'}
                size="lg"
                disabled={plan.status !== 'pending_approval'}
                onClick={() => handleActionClick(plan.id, 'draft', 'rejected')}
              >
                差戻し
              </Button>

              <hr className="border-gray-400" />
            </div>
          </Disclosure>

          {openDialogId === plan.id && dialogType === 'draft' && status === 'rejected' && (
            <UpdateDialog
              isOpen={true}
              setIsOpen={(isOpen) => !isOpen && setOpenDialogId(null)}
              afterSubmit={() => {
                fetchAllPlans();
                setOpenDialogId(null);
              }}
              childID={childID}
              supportPlanID={plan.id}
              status={status}
              lineAccessToken={lineUser?.accessToken ?? ''}
              target={formattedDate(
                new Date(plan.shared_date || plan.created_date),
                'yyyy年M月d日'
              )}
              planType="draft"
            />
          )}
        </div>
      ))}

      {/* 個別支援計画の表示 */}
      {supportPlans.map((plan) => (
        <div
          key={`plan-${plan.id}`}
          className="rounded-8 bg-white p-4 shadow"
        >
          <Disclosure className="h-full rounded-md bg-gray-100">
            <div className="space-y-4 p-4">
              <div className="text-base font-bold">個別支援計画</div>
              {plan.shared_date && (
                <>
                  <div className="text-base font-bold">共有日</div>
                  <div className="text-base">
                    {formattedDate(new Date(plan.shared_date), 'yyyy年M月d日')}
                  </div>
                </>
              )}
              <hr className="border-gray-400" />
            </div>

            <div className="space-y-4 p-4">
              <a
                href="#"
                onClick={(e) => handlePDFClick(e, plan.pdf_url)}
                className="text-base underline"
              >
                {formattedDate(new Date(plan.created_date), 'yyyy年M月d日')}
              </a>

              <Button
                className="w-full"
                variant={plan.status === 'pending_approval' ? 'solid-fill' : 'disabled'}
                size="lg"
                type="submit"
                disabled={plan.status !== 'pending_approval'}
                onClick={() => handleActionClick(plan.id, 'plan', 'approved')}
              >
                承認する
              </Button>

              <Button
                className="w-full"
                variant={plan.status === 'pending_approval' ? 'text' : 'disabled'}
                size="lg"
                disabled={plan.status !== 'pending_approval'}
                onClick={() => handleActionClick(plan.id, 'plan', 'rejected')}
              >
                差戻し
              </Button>

              <hr className="border-gray-400" />
            </div>
          </Disclosure>

          {openDialogId === plan.id && dialogType === 'plan' && status === 'rejected' && (
            <UpdateDialog
              isOpen={true}
              setIsOpen={(isOpen) => !isOpen && setOpenDialogId(null)}
              afterSubmit={() => {
                fetchAllPlans();
                setOpenDialogId(null);
              }}
              childID={childID}
              supportPlanID={plan.id}
              status={status}
              lineAccessToken={lineUser?.accessToken ?? ''}
              target={formattedDate(
                new Date(plan.shared_date || plan.created_date),
                'yyyy年M月d日'
              )}
              planType="plan"
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default SupportPlanPage;
